import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { query, collection, orderBy, onSnapshot } from 'firebase/firestore';

// ◄ CORRECCIÓN: Rutas relativas recalibradas de forma milimétrica
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth';
import { getPersonaChipClass, getSiniestroStatusChip } from '../../../../utils/styles.js';

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../common/SortableHeader';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';
import Icons from '../common/Icons';

// MODALES EXISTENTES EN EL PROYECTO
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza.jsx';
import ModalFichaCliente from '../../../ui/ModalFichaCliente.jsx';
import ModalConfirmacion from '../../../ui/ModalConfirmacion.jsx';
import ModalPostVenta from '../../../ui/ModalPostVenta.jsx'; 

// FILTROS Y CONTROLADOR
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';
import { usePostventaTable } from './usePostventaTable';

// --- AUXILIAR DE FECHAS SEGURO ---
const renderSmartDate = (val) => {
    if (!val) return '-';
    if (typeof val === 'string' && val.includes('-')) return val.split('-').reverse().join('/');
    if (val.seconds) return new Date(val.seconds * 1000).toLocaleDateString('es-AR');
    return String(val);
};

// --- SUB-COMPONENTE INTERNO MEMOIZADO PARA LEGAJO DE HISTORIAL ---
const PostVentaHistory = memo(({ polizaPath }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!polizaPath) return;
        const q = query(collection(db, `${polizaPath}/gestiones_postventa`), orderBy('fechaCreacion', 'desc'));
        const unsubscribe = onSnapshot(q, (snap) => {
            setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [polizaPath]);

    if (loading) return <div className="py-4 text-center text-[10px] font-bold text-slate-400 animate-pulse uppercase">Sincronizando legajo...</div>;

    return (
        <div className="space-y-4 text-left">
            <h4 className="text-[11px] font-black text-indigo-900 uppercase tracking-widest flex items-center gap-2 text-left">
                <Icons.History className="w-4 h-4 text-indigo-600" /> Historial de Etapas
            </h4>
            <div className="flex flex-col gap-2">
                {history.length > 0 ? history.map((h, i) => (
                    <div key={h.id || i} className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:border-indigo-200 transition-all text-left">
                        <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-2.5 py-1.5 rounded-lg tabular-nums border border-indigo-100/50 min-w-[95px] text-center">
                            {renderSmartDate(h.fechaCreacion)}
                        </span>
                        <div className="min-w-[180px]">
                            <span className="text-[10px] font-black text-slate-800 uppercase tracking-tight">{h.tipoGestion || 'Gestión'}</span>
                        </div>
                        <p className="text-sm text-slate-500 italic flex-grow">"{h.comentario}"</p>
                        <div className="flex items-center gap-3">
                            <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase ${getPersonaChipClass(h.quienGestiono)}`}>
                                {h.quienGestiono || 'S/D'}
                            </span>
                        </div>
                    </div>
                )) : (
                    <div className="p-10 bg-slate-50 rounded-3xl border border-dashed border-slate-200 text-center text-sm text-slate-400 italic">No hay gestiones registradas para este caso.</div>
                )}
            </div>
        </div>
    );
});
PostVentaHistory.displayName = 'PostVentaHistory';

// --- FILA MEMOIZADA ---
const TableRow = memo(({ item, isSelected, isExpanded, isLocked, isLockedByMe, columnVisibility, onSelect, onToggleExpand, onAction, onUnlock }) => {
    const activeColumnsCount = useMemo(() => {
        const toggledCount = Object.values(columnVisibility).filter(v => v).length;
        return toggledCount + 2; // +1 chevron, +1 acciones
    }, [columnVisibility]);

    return (
        <>
            <tr 
                onClick={() => !isLocked && onToggleExpand(item.id)} 
                className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 
                    ${isLocked ? 'opacity-40 select-none bg-slate-50' : ''}
                    ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'} 
                    ${isExpanded ? 'bg-slate-50' : ''}`}
            >
                <td className={`p-2.5 w-12 text-center border-b border-r border-slate-100 sticky left-0 z-[45] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] ${isExpanded ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'}`}>
                    <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-90 text-indigo-600' : 'text-slate-300'}`}>
                        <Icons.ChevronRight />
                    </div>
                </td>
                
                {columnVisibility.fechaCarga && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-slate-500 tabular-nums">{renderSmartDate(item.fechaCarga)}</td>}
                {columnVisibility.displayNombre && <td className="p-2.5 px-4 text-sm text-slate-900 font-bold border-b border-r border-slate-100 truncate max-w-[280px] text-left uppercase">{item.displayNombre}</td>}
                {columnVisibility.displayDoc && <td className="p-2.5 px-3 text-sm text-slate-500 font-mono text-center border-b border-r border-slate-100">{item.displayDoc}</td>}
                {columnVisibility.nPoliza && <td className="p-2.5 px-3 text-sm text-slate-900 font-bold text-center border-b border-r border-slate-100 tabular-nums">{item.nPoliza || '-'}</td>}
                
                {columnVisibility.compania && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                        <div className="flex items-center justify-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-lg w-fit mx-auto shadow-sm">
                            <Icons.Company />
                            <span className="text-[10px] text-blue-700 font-black uppercase">{item.compania || '-'}</span>
                        </div>
                    </td>
                )}
                {columnVisibility.riesgo && (
                    <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                        <div className="flex flex-col text-left">
                            <span className="font-bold text-slate-800 tracking-tight">{item.riesgo || '-'}</span>
                            <span className="text-[11px] font-medium text-slate-500 uppercase truncate">{item.subriesgo || 'General'}</span>
                        </div>
                    </td>
                )}
                {columnVisibility.postventa_numero_gestion && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-[11px] font-black text-indigo-600 tabular-nums">{item.postventa_numero_gestion || '-'}</td>}
                
                {columnVisibility.postventa_estado && (
                    <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                        <span className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase border ${getSiniestroStatusChip(item.postventa_estado || 'Abierto')}`}>
                            {item.postventa_estado || 'Abierto'}
                        </span>
                    </td>
                )}
                {columnVisibility.postventa_quienGestiono && (
                    <td className="p-2.5 px-3 text-center border-b border-r border-slate-100">
                        {item.postventa_quienGestiono ? (
                            <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit mx-auto ${getPersonaChipClass(item.postventa_quienGestiono)}`}>
                                <span className="text-[10px] font-bold uppercase">{item.postventa_quienGestiono}</span>
                            </div>
                        ) : '-'}
                    </td>
                )}
                {columnVisibility.postventa_ultimoTipoGestion && (
                    <td className="p-2.5 px-3 text-center border-b border-r border-slate-100">
                        <span className="text-[10px] font-black text-slate-600 uppercase bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
                            {item.postventa_ultimoTipoGestion || '-'}
                        </span>
                    </td>
                )}
                {columnVisibility.postventa_fechaProximaGestion && (
                    <td className="p-2.5 px-3 text-sm text-center border-b border-r border-slate-100 bg-indigo-50/10">
                        <span className="font-black text-indigo-700 tabular-nums">
                            {renderSmartDate(item.postventa_fechaProximaGestion)}
                        </span>
                    </td>
                )}
                {columnVisibility.canal && (
                    <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                        <div className="flex flex-col text-left">
                            <span className="font-bold text-slate-800 tracking-tight">{item.canal || '-'}</span>
                            <span className="text-[11px] font-medium text-slate-500 uppercase truncate">{item.displayCanalNombre || '-'}</span>
                        </div>
                    </td>
                )}

                {/* Acciones */}
                <td className={`p-2.5 px-4 text-right sticky right-0 transition-colors z-[45] border-b border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.05)] ${isExpanded ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'}`}>
                    <div className="flex justify-end items-center gap-1.5 text-left">
                        <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'poliza'); }} className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all" title="Ver Póliza"><Icons.Policy /></button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'ficha'); }} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Ficha Cliente"><Icons.User /></button>
                        
                        {isLocked ? (
                            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100 animate-pulse shadow-sm">
                                <Icons.Lock /> <span className="text-[9px] font-black uppercase tracking-tighter">{item.bloqueo?.userName || 'Ocupado'}</span>
                            </div>
                        ) : (
                            <>
                                <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'edit'); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Gestionar PostVenta"><Icons.Edit /></button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'delete'); }} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Eliminar PostVenta"><Icons.Trash /></button>
                                {isLockedByMe && (
                                    <button onClick={(e) => { e.stopPropagation(); onUnlock(item.path); }} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Liberar Caso">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </td>
            </tr>
            {isExpanded && !isLocked && (
                <tr className="bg-slate-50/80 border-b border-slate-200 shadow-inner">
                    <td colSpan={activeColumnsCount} className="p-0 overflow-hidden relative">
                         <div className="sticky left-0 w-[calc(100vw-80px)] md:w-full max-w-[1400px] p-8 md:p-10 animate-fade-in text-left">
                            <PostVentaHistory polizaPath={item.path} />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
});
TableRow.displayName = 'TableRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const GestionPostventa = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Consumimos nuestro cerebro de negocio
    const {
        loading,
        clientsCache,
        activeItem,
        showEditModal,
        showPolicyModal,
        showFichaModal,
        showDeleteConfirm,
        tableState,
        itemsTable,
        activeItems,
        activeItemPathRef,
        setShowEditModal,
        setShowPolicyModal,
        setShowFichaModal,
        setShowDeleteConfirm,
        setActiveItem,
        handleAction,
        confirmDelete,
        unlockPoliza
    } = usePostventaTable();

    // Desestructuramos el estado síncrono unificado
    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        expandedRowId,
        setExpandedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange,
        toggleRowExpansion,
        checkIsLocked: checkIsItemLocked
    } = tableState;

    const columnLabels = {
        fechaCarga: "Carga", displayNombre: "Cliente", displayDoc: "DNI/CUIT", nPoliza: "N° Póliza",
        compania: "Compañía", riesgo: "Riesgo/Sub", postventa_numero_gestion: "N. Gestión",
        postventa_estado: "Estado", postventa_quienGestiono: "Gestionó",
        postventa_ultimoTipoGestion: "Etapa", postventa_fechaProximaGestion: "Próxima", canal: "Canal"
    };

    // Liberación por descarga involuntaria de pestaña
    useEffect(() => {
        const handleUnload = () => { if (activeItemPathRef.current) unlockPoliza(activeItemPathRef.current); };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [unlockPoliza, activeItemPathRef]);

    // Configuración de Filtros Dinámicos (Claves mapeadas $100% al objeto de la tabla)
    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(p => p[key]))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        
        const filteredByRiesgo = filterValues.riesgo?.length > 0 ? itemsTable.filter(i => filterValues.riesgo.includes(i.riesgo)) : itemsTable;
        const filteredByCanal = filterValues.canal?.length > 0 ? itemsTable.filter(i => filterValues.canal.includes(i.canal)) : itemsTable;

        return [
            { name: 'search', label: 'Buscador Global', type: 'text', placeholder: 'Cliente, DNI, Póliza o Gestión...', className: 'w-80' },
            { name: 'compania', label: 'Compañía', type: 'select', isMulti: true, options: getUnique('compania') },
            { name: 'riesgo', label: 'Riesgo', type: 'select', isMulti: true, options: getUnique('riesgo') },
            { name: 'subriesgo', label: 'Subriesgo', type: 'select', isMulti: true, options: getUnique('subriesgo', filteredByRiesgo) },
            { name: 'postventa_quienGestiono', label: 'Responsable', type: 'select', isMulti: true, options: getUnique('postventa_quienGestiono') },
            { name: 'postventa_ultimoTipoGestion', label: 'Etapa', type: 'select', isMulti: true, options: getUnique('postventa_ultimoTipoGestion') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique('canal') },
            { name: 'displayCanalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: getUnique('displayCanalNombre', filteredByCanal) },
            { name: 'subcanal', label: 'Subcanal / Asociado', type: 'select', isMulti: true, options: getUnique('subcanal', filteredByCanal) },
            { name: 'postventa_fechaProximaGestion', label: 'Próxima Gestión', type: 'date-range' },
        ];
    }, [itemsTable, filterValues]);

    if (loading) return (
        <div className="flex h-screen flex-col items-center justify-center bg-white space-y-6 text-left">
            <div className="w-16 h-16 border-4 border-indigo-600/20 border-t-indigo-600 rounded-full animate-spin"></div>
            <div className="flex flex-col items-center gap-2 text-center text-slate-900">
                <span className="font-black text-[10px] uppercase tracking-[0.3em] text-indigo-600 animate-pulse">Sincronizando PostVenta</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-8">Preparando legajos, pólizas e identidades...</span>
            </div>
        </div>
    );

    return (
        <div className="flex h-screen w-full flex-col overflow-hidden bg-white font-['Inter'] antialiased text-left text-slate-900">
            <div className="h-1 w-full bg-indigo-600 shrink-0"></div>
            
            {/* CABECERA UNIFICADA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button type="button" onClick={() => navigate('/cpanel')} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm"><Icons.ArrowLeft /></button>
                    <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">Tablero de PostVenta</h2>
                </div>
                <div className="flex items-center gap-4">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm">
                        <div className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-lg font-black tabular-nums">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Casos Activos</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS CON KEY ESTABLE */}
            <FiltrosDinamicos 
                key="postventa-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA CONTENEDORA COMÚN */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white">
                <TableContainer minWidth="3200px">
                    <thead>
                        <tr className="bg-[#01032d] text-white">
                            <th className="w-12 border-b border-r border-slate-700 bg-slate-900 sticky left-0 top-0 z-[60] shadow-[2px_0_5px_rgba(0,0,0,0.2)]"></th>
                            <SortableHeader label="Carga" sortKey="fechaCarga" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Cliente" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[220px] text-left" />
                            <SortableHeader label="DNI / CUIT" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="N° Póliza" sortKey="nPoliza" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Compañía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-center" />
                            <SortableHeader label="Riesgo / Sub" sortKey="riesgo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[180px] text-left" />
                            <SortableHeader label="N. Gestión" sortKey="postventa_numero_gestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Estado" sortKey="postventa_estado" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Responsable" sortKey="postventa_quienGestiono" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Etapa" sortKey="postventa_ultimoTipoGestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Próxima" sortKey="postventa_fechaProximaGestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center bg-indigo-900/50" />
                            <SortableHeader label="Canal / Nombre" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-left" />
                            <th className="w-28 p-4 sticky right-0 top-0 bg-slate-900 border-b border-slate-700 z-[65] uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {paginatedData.map(item => {
                            const isLocked = checkIsItemLocked(item, user?.uid);
                            const isLockedByMe = item.bloqueo && item.bloqueo.userId === user?.uid;

                            return (
                                <TableRow 
                                    key={item.id} 
                                    item={item} 
                                    isSelected={selectedRowId === item.id} 
                                    isExpanded={expandedRowId === item.id}
                                    isLocked={isLocked}
                                    isLockedByMe={isLockedByMe}
                                    columnVisibility={columnVisibility}
                                    onSelect={setSelectedRowId} 
                                    onToggleExpand={toggleRowExpansion}
                                    onAction={handleAction} 
                                    onUnlock={unlockPoliza}
                                />
                            );
                        })}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN COMÚN */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="casos"
                    accentClass="text-indigo-900 bg-indigo-50 border-indigo-100"
                />
            </main>

            {/* MODALES RECONECTADOS */}
            {activeItem && (
                <>
                    <ModalFichaCliente 
                        show={showFichaModal} 
                        onClose={() => { setShowFichaModal(false); setActiveItem(null); }} 
                        client={{ id: activeItem.clienteId, ...(clientsCache[activeItem.clienteId] || {}) }} 
                    />
                    <ModalDetallePoliza 
                        show={showPolicyModal} 
                        onClose={() => { setShowPolicyModal(false); setActiveItem(null); }} 
                        poliza={activeItem} 
                    />
                    <ModalPostVenta 
                        show={showEditModal} 
                        onClose={() => { unlockPoliza(activeItem.path); setShowEditModal(false); setActiveItem(null); }} 
                        onSuccess={() => { unlockPoliza(activeItem.path); setShowEditModal(false); setActiveItem(null); }}
                        poliza={activeItem} 
                    />
                    <ModalConfirmacion 
                        show={showDeleteConfirm} 
                        onClose={() => { setShowDeleteConfirm(false); setActiveItem(null); }} 
                        onConfirm={confirmDelete} 
                        title="Eliminar Gestión PostVenta" 
                        message="¿Estás seguro? Se borrará todo el historial de gestiones y la póliza saldrá de este tablero." 
                    />
                </>
            )}
        </div>
    );
};

export default GestionPostventa;